import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

const RunnersTable = ({ horses }) => {
  if (!horses || horses.length === 0) {
    return (
      <div className="text-center text-[#52525B] py-4">
        No runner data available
      </div>
    );
  }

  // Sort by visible position
  const sortedHorses = [...horses].sort((a, b) => a.visible_position - b.visible_position);

  return (
    <ScrollArea className="h-[400px]" data-testid="runners-table">
      <table className="runner-table">
        <thead>
          <tr>
            <th className="w-10">Pos</th>
            <th>Horse</th>
            <th className="w-12 text-centre">True</th>
            <th className="w-16 text-right">Eff.</th>
            <th className="w-16 text-right">Press.</th>
            <th className="w-12 text-centre">Mom.</th>
            <th className="w-20">Verdict</th>
          </tr>
        </thead>
        <tbody>
          {sortedHorses.map((horse, index) => (
            <tr 
              key={horse.horse_id} 
              className="table-row-transition"
              data-testid={`runner-row-${horse.saddle_number}`}
            >
              {/* Position */}
              <td className="font-data text-[#F97316] font-bold">
                {horse.visible_position}
              </td>
              
              {/* Horse Name */}
              <td>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#242424] flex items-center justify-center text-xs font-data text-[#A1A1AA]">
                    {horse.saddle_number}
                  </span>
                  <div>
                    <div className="font-medium text-white text-sm">
                      {horse.horse_name}
                    </div>
                    <div className="text-[#52525B] text-xs">
                      {horse.state}
                    </div>
                  </div>
                </div>
              </td>
              
              {/* True Position */}
              <td className="text-centre">
                <span className={`font-data text-sm ${
                  horse.true_position < horse.visible_position 
                    ? "text-[#10B981]" 
                    : horse.true_position > horse.visible_position
                    ? "text-[#EF4444]"
                    : "text-[#A1A1AA]"
                }`}>
                  {horse.true_position}
                </span>
              </td>
              
              {/* Efficiency */}
              <td className="text-right">
                <div className="flex flex-col items-end">
                  <span className="font-data text-sm text-white">
                    {horse.efficiency_score.toFixed(0)}%
                  </span>
                  <span className={`text-xs ${
                    horse.efficiency_band === "Efficient" 
                      ? "text-[#10B981]" 
                      : horse.efficiency_band === "Overworked"
                      ? "text-[#EF4444]"
                      : "text-[#A1A1AA]"
                  }`}>
                    {horse.efficiency_band}
                  </span>
                </div>
              </td>
              
              {/* Pressure */}
              <td className="text-right">
                <div className="flex flex-col items-end">
                  <span className="font-data text-sm text-white">
                    {horse.pressure_score.toFixed(0)}
                  </span>
                  <span className={`text-xs ${
                    horse.pressure_band === "High" 
                      ? "text-[#EF4444]" 
                      : horse.pressure_band === "Moderate"
                      ? "text-[#F59E0B]"
                      : "text-[#A1A1AA]"
                  }`}>
                    {horse.pressure_band}
                  </span>
                </div>
              </td>
              
              {/* Momentum */}
              <td className="text-centre">
                <MomentumIndicator band={horse.momentum_band} />
              </td>
              
              {/* Verdict */}
              <td>
                <VerdictBadge verdict={horse.verdict} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollArea>
  );
};

const MomentumIndicator = ({ band }) => {
  if (band === "Building") {
    return (
      <div className="flex items-center justify-center text-[#10B981]">
        <TrendingUp className="w-4 h-4" />
      </div>
    );
  }
  if (band === "Dropping") {
    return (
      <div className="flex items-center justify-center text-[#EF4444]">
        <TrendingDown className="w-4 h-4" />
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center text-[#52525B]">
      <Minus className="w-4 h-4" />
    </div>
  );
};

const VerdictBadge = ({ verdict }) => {
  const classes = {
    Strong: "verdict-strong",
    Watch: "verdict-watch",
    Risk: "verdict-risk",
    Forgive: "verdict-forgive",
  };

  return (
    <span className={classes[verdict] || "verdict-risk"}>
      {verdict}
    </span>
  );
};

export default RunnersTable;
